import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar, FileText, Download, Share2, ArrowLeft, Clock, 
  Users, BarChart3, Star, CheckCircle2, X, ChevronRight,
  Lightbulb, TrendingUp, Target, Award, Eye, Bookmark
} from 'lucide-react';

// Import existing Lusidea components
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Color palette and spacing from Lusidea design system
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  secondaryLighter: '#fffbf0',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

const SPACING = {
  xs: 'px-3 py-1.5',
  sm: 'px-4 py-2.5',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-14 md:py-20 lg:py-24',
  sectionMedium: 'py-10 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
};

// Button component matching Lusidea design
const Button = ({ children, onClick, variant = 'primary', size = 'md', icon: Icon, className = '', href, ...props }) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
  
  const variants = {
    primary: 'bg-primary text-white hover:bg-primaryDark focus-visible:ring-primary shadow-md hover:shadow-lg',
    secondary: 'bg-secondary text-black hover:bg-secondaryDark focus-visible:ring-secondary shadow-md hover:shadow-lg',
    outline: 'border-2 border-primary text-primary hover:bg-primaryLight focus-visible:ring-primary',
    outlineGray: 'border border-lightGray text-darkGray hover:bg-offWhite focus-visible:ring-mediumGray'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg'
  };
  
  const Component = href ? 'a' : 'button';
  const linkProps = href ? { href } : {};
  
  return (
    <Component 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...linkProps}
      {...props}
    >
      {children}
      {Icon && <Icon size={size === 'lg' ? 20 : size === 'sm' ? 14 : 16} className="ml-2" />}
    </Component>
  );
};

// Toast Component
const Toast = React.memo(({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = type === 'success' ? COLORS.successLight : COLORS.primaryLight;
  const textColor = type === 'success' ? COLORS.success : COLORS.primary;
  const icon = type === 'success' ? <CheckCircle2 size={18} /> : <ChevronRight size={18} />;
  
  return (
    <div 
      className="fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border max-w-sm flex items-center gap-3"
      style={{ backgroundColor: bgColor, borderColor: COLORS.lightGray + '20' }}
      role="alert"
      aria-live="assertive"
    >
      <span style={{ color: textColor }} aria-hidden="true">{icon}</span>
      <p className="font-medium" style={{ color: COLORS.darkGray }}>{message}</p>
      <button 
        onClick={onClose} 
        className="ml-auto transition-colors"
        style={{ color: COLORS.mediumGray }}
        onMouseOver={(e) => e.target.style.color = COLORS.primary}
        onMouseOut={(e) => e.target.style.color = COLORS.mediumGray}
        aria-label="Close notification"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
});

Toast.displayName = 'Toast';

// Main Report Detail Component
// Usage examples:
// 1. With React Router: <ReportDetail slug={useParams().slug} />
// 2. With Next.js: <ReportDetail slug={router.query.slug} />
// 3. Auto-extract from URL: <ReportDetail />
const ReportDetail = ({ slug }) => {
  // All hooks must be called at the top level, before any early returns
  const [report, setReport] = useState(null);
  const [relatedReports, setRelatedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);

  // Get slug from props (passed from routing) or URL
  const reportSlug = useMemo(() => {
    if (slug) return slug;
    
    // Extract slug from current URL path
    const pathParts = window.location.pathname.split('/');
    return pathParts[pathParts.length - 1];
  }, [slug]);

  // Update page title when report loads
  useEffect(() => {
    if (report) {
      document.title = `${report.title} | Lusidea Reports`;
    }
  }, [report]);

  useEffect(() => {
    // Handle case where no slug is available
    if (!reportSlug || reportSlug === 'reports') {
      window.location.href = '/reports';
      return;
    }

    const loadReport = async () => {
      try {
        const response = await fetch('/data/reports.json');
        if (!response.ok) throw new Error('Failed to load reports');
        const data = await response.json();
        
        // Find current report by slug
        const currentReport = data.reports?.find(r => r.slug === reportSlug);
        if (!currentReport) {
          setError('Report not found');
          return;
        }
        
        setReport(currentReport);
        
        // Find related reports (same category, different report, limit to 3)
        const related = data.reports?.filter(r => 
          r.category === currentReport.category && r.id !== currentReport.id
        ).slice(0, 3) || [];
        
        setRelatedReports(related);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
    window.scrollTo(0, 0);
  }, [reportSlug]);

  const handleDownload = useCallback(() => {
    if (!report) return;
    
    const link = document.createElement('a');
    link.href = `/pdfs/${report.pdfFilename}`;
    link.download = report.pdfFilename;
    link.click();
    
    setToast({
      message: `Downloading ${report.title}`,
      type: 'success'
    });
  }, [report]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: report.title,
          text: report.shortDescription,
          url: url
        });
        setToast({
          message: 'Report shared successfully',
          type: 'success'
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  }, [report]);

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setToast({
        message: 'Link copied to clipboard',
        type: 'success'
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setToast({
        message: 'Failed to copy link',
        type: 'error'
      });
    }
  }, []);

  const formatDate = useCallback((dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateStr;
    }
  }, []);

  // Handle early returns after all hooks have been called
  if (!reportSlug || reportSlug === 'reports') {
    return null; // Will redirect via useEffect
  }

  if (loading) {
    return (
      <div className="font-sans antialiased bg-white overflow-x-hidden min-h-screen" style={{ color: COLORS.darkGray }}>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4" style={{ 
              borderColor: COLORS.primary, 
              borderTopColor: 'transparent' 
            }}></div>
            <p style={{ color: COLORS.mediumGray }}>Loading report...</p>
          </div>
        </div>
        <FooterLan />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="font-sans antialiased bg-white overflow-x-hidden min-h-screen" style={{ color: COLORS.darkGray }}>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg border" style={{ borderColor: COLORS.lightGray }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: COLORS.primaryLight }}>
              <X size={24} style={{ color: COLORS.primary }} />
            </div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.black }}>
              {error === 'Report not found' ? 'Report Not Found' : 'Error Loading Report'}
            </h2>
            <p className="mb-6" style={{ color: COLORS.mediumGray }}>{error}</p>
            <Button href="/reports" variant="primary" size="md">
              Back to Reports
            </Button>
          </div>
        </div>
        <FooterLan />
      </div>
    );
  }

  return (
    <div className="font-sans antialiased bg-white overflow-x-hidden min-h-screen" style={{ color: COLORS.darkGray }}>
      <HeaderNav />
      
      {/* Toast notifications */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      <main id="main-content" tabIndex="-1">
        {/* Breadcrumb */}
        <div className="pt-20 pb-4 border-b" style={{ backgroundColor: COLORS.white, borderColor: COLORS.lightGray }}>
          <div className={SPACING.container}>
            <nav className="flex items-center text-sm" style={{ color: COLORS.mediumGray }}>
              <a href="/" className="transition-colors" onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.mediumGray}>Home</a>
              <span className="mx-2">/</span>
              <a href="/reports" className="transition-colors" onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.mediumGray}>Reports</a>
              <span className="mx-2">/</span>
              <span className="font-medium truncate" style={{ color: COLORS.black }}>{report.title}</span>
            </nav>
          </div>
        </div>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, ${COLORS.secondaryLight} 100%)`
        }}>
          <div className={`${SPACING.container} py-12`}>
            <div className="max-w-4xl mx-auto">
              {/* Back Button */}
              <div className="mb-6">
                <Button 
                  href="/reports" 
                  variant="outlineGray" 
                  size="sm" 
                  icon={ArrowLeft}
                  className="mb-4"
                >
                  Back to Reports
                </Button>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold" style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primary }}>
                  {report.category}
                </span>
                {report.timeHorizon && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}>
                    {report.timeHorizon}
                  </span>
                )}
                {report.confidenceLevel && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                    {report.confidenceLevel} Confidence
                  </span>
                )}
                {report.featured && (
                  <span className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1" style={{ backgroundColor: COLORS.secondaryLight, color: COLORS.secondaryDark }}>
                    <Star size={12} fill="currentColor" />
                    Featured
                  </span>
                )}
                {report.premium && (
                  <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: COLORS.primary, color: COLORS.white }}>
                    Premium
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: COLORS.black }}>
                {report.title}
              </h1>

              {/* Description */}
              <p className="text-lg mb-8 leading-relaxed" style={{ color: COLORS.darkGray }}>
                {report.longDescription || report.shortDescription}
              </p>

              {/* Meta info */}
              <div className="grid sm:grid-cols-4 gap-6 mb-8 p-6 bg-white/80 rounded-xl border" style={{ borderColor: COLORS.lightGray }}>
                <div className="text-center">
                  <div className="mb-2" style={{ color: COLORS.primary }}>
                    <Calendar className="w-6 h-6 mx-auto" />
                  </div>
                  <div className="text-sm" style={{ color: COLORS.mediumGray }}>Published</div>
                  <div className="font-semibold">{formatDate(report.publishedDate)}</div>
                </div>
                <div className="text-center">
                  <div className="mb-2" style={{ color: COLORS.primary }}>
                    <FileText className="w-6 h-6 mx-auto" />
                  </div>
                  <div className="text-sm" style={{ color: COLORS.mediumGray }}>Pages</div>
                  <div className="font-semibold">{report.pageCount}</div>
                </div>
                <div className="text-center">
                  <div className="mb-2" style={{ color: COLORS.primary }}>
                    <Download className="w-6 h-6 mx-auto" />
                  </div>
                  <div className="text-sm" style={{ color: COLORS.mediumGray }}>Downloads</div>
                  <div className="font-semibold">{(report.downloadCount || 0).toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="mb-2" style={{ color: COLORS.primary }}>
                    <BarChart3 className="w-6 h-6 mx-auto" />
                  </div>
                  <div className="text-sm" style={{ color: COLORS.mediumGray }}>File Size</div>
                  <div className="font-semibold">{report.fileSize}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={handleDownload}
                  variant="primary"
                  size="lg"
                  icon={Download}
                  className="shadow-lg"
                >
                  Download Report ({report.fileSize})
                </Button>
                <Button
                  onClick={handleShare}
                  variant="outline"
                  size="lg"
                  icon={Share2}
                >
                  {copied ? 'Link Copied!' : 'Share Report'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`${SPACING.container} py-16`}>
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Key Insights */}
              {report.keyInsights && report.keyInsights.length > 0 && (
                <ContentSection 
                  title="Key Strategic Insights"
                  icon={<Lightbulb size={24} style={{ color: COLORS.secondary }} />}
                  items={report.keyInsights}
                  bgColor="#ecfdf5"
                  borderColor="#d1fae5"
                  numbered={true}
                />
              )}

              {/* Trend Highlights */}
              {report.trendHighlights && report.trendHighlights.length > 0 && (
                <ContentSection 
                  title="Emerging Trend Highlights"
                  icon={<TrendingUp size={24} style={{ color: COLORS.primary }} />}
                  items={report.trendHighlights}
                  bgColor="#dbeafe"
                  borderColor="#bfdbfe"
                />
              )}

              {/* Future Implications */}
              {report.futureImplications && report.futureImplications.length > 0 && (
                <ContentSection 
                  title="Future Implications"
                  icon={<Eye size={24} style={{ color: '#7c3aed' }} />}
                  items={report.futureImplications}
                  bgColor="#f3e8ff"
                  borderColor="#e9d5ff"
                />
              )}

              {/* Actionable Recommendations */}
              {report.actionableRecommendations && report.actionableRecommendations.length > 0 && (
                <ContentSection 
                  title="Actionable Recommendations"
                  icon={<Target size={24} style={{ color: COLORS.primary }} />}
                  items={report.actionableRecommendations}
                  bgColor={COLORS.offWhite}
                  borderColor={COLORS.lightGray}
                  numbered={true}
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Report Summary */}
              <div className="bg-white rounded-lg border p-6 mb-8 sticky top-8" style={{ borderColor: COLORS.lightGray }}>
                <h3 className="text-lg font-bold mb-4 flex items-center" style={{ color: COLORS.black }}>
                  <BarChart3 className="mr-3" size={20} style={{ color: COLORS.primary }} />
                  Report Summary
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm mb-1" style={{ color: COLORS.mediumGray }}>Category</div>
                    <div className="font-medium">{report.category}</div>
                  </div>
                  {report.timeHorizon && (
                    <div>
                      <div className="text-sm mb-1" style={{ color: COLORS.mediumGray }}>Time Horizon</div>
                      <div className="font-medium">{report.timeHorizon}</div>
                    </div>
                  )}
                  {report.confidenceLevel && (
                    <div>
                      <div className="text-sm mb-1" style={{ color: COLORS.mediumGray }}>Confidence Level</div>
                      <div className="font-medium" style={{ color: COLORS.success }}>{report.confidenceLevel}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm mb-1" style={{ color: COLORS.mediumGray }}>Key Insights</div>
                    <div className="font-medium">{report.keyInsights?.length || 0} strategic insights</div>
                  </div>
                  {report.rating && (
                    <div>
                      <div className="text-sm mb-1" style={{ color: COLORS.mediumGray }}>Rating</div>
                      <div className="font-medium flex items-center gap-1">
                        <Star size={14} style={{ color: COLORS.secondary }} fill="currentColor" />
                        {report.rating}/5.0
                      </div>
                    </div>
                  )}
                  <div className="pt-4 border-t" style={{ borderColor: COLORS.lightGray }}>
                    <div className="text-sm mb-1" style={{ color: COLORS.mediumGray }}>File Details</div>
                    <div className="text-sm" style={{ color: COLORS.darkGray }}>
                      {report.pageCount} pages • {report.fileSize} • PDF format
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {report.tags && report.tags.length > 0 && (
                <div className="bg-white rounded-lg border p-6" style={{ borderColor: COLORS.lightGray }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center" style={{ color: COLORS.black }}>
                    <Bookmark className="mr-3" size={20} style={{ color: COLORS.secondary }} />
                    Related Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {report.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 text-sm font-medium rounded-full" style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primary }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related Reports */}
        {relatedReports.length > 0 && (
          <div className="py-16" style={{ backgroundColor: COLORS.white }}>
            <div className={SPACING.container}>
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12" style={{ color: COLORS.black }}>
                Related <span style={{ color: COLORS.primary }}>Reports</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {relatedReports.map(relatedReport => (
                  <RelatedReportCard key={relatedReport.id} report={relatedReport} />
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <FooterLan />
    </div>
  );
};

// Content Section Component
const ContentSection = React.memo(({ title, icon, items, bgColor, borderColor, numbered = false }) => (
  <div className="mb-12">
    <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: COLORS.black }}>
      <span className="mr-3">{icon}</span>
      {title}
    </h2>
    <div className="grid gap-4">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-4 p-5 rounded-lg border" style={{ backgroundColor: bgColor, borderColor }}>
          {numbered && (
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: COLORS.primary, color: COLORS.white }}>
              {index + 1}
            </div>
          )}
          <p className="font-medium" style={{ color: COLORS.darkGray }}>{item}</p>
        </div>
      ))}
    </div>
  </div>
));

ContentSection.displayName = 'ContentSection';

// Related Report Card Component
const RelatedReportCard = React.memo(({ report }) => (
  <div className="bg-white rounded-lg border overflow-hidden hover:shadow-md transition-shadow" style={{ borderColor: COLORS.lightGray }}>
    <div 
      className="h-32 flex items-center justify-center"
      style={{
        background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, ${COLORS.secondaryLight} 100%)`
      }}
    >
      <div className="text-center">
        <div className="w-12 h-12 bg-white/90 rounded-lg flex items-center justify-center mb-2">
          <FileText className="w-6 h-6" style={{ color: COLORS.primary }} />
        </div>
        <div className="bg-white/90 rounded px-2 py-1">
          <p className="text-xs font-semibold line-clamp-2" style={{ color: COLORS.black }}>
            {report.title.length > 30 ? report.title.substring(0, 30) + '...' : report.title}
          </p>
        </div>
      </div>
    </div>
    
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primary }}>
          {report.category}
        </span>
        {report.featured && (
          <Star size={12} style={{ color: COLORS.secondary }} fill="currentColor" />
        )}
      </div>
      <h3 className="text-lg font-bold mt-2 mb-2 line-clamp-2" style={{ color: COLORS.black }}>
        {report.title}
      </h3>
      <p className="text-sm mb-4 line-clamp-2" style={{ color: COLORS.darkGray }}>
        {report.shortDescription}
      </p>
      <Button 
        onClick={() => window.location.href = `/reports/${report.slug}`}
        variant="primary"
        size="md"
        className="w-full"
      >
        View Report
      </Button>
    </div>
  </div>
));

RelatedReportCard.displayName = 'RelatedReportCard';

export default ReportDetail;